import { CurrencyAmount, JSBI, Token, Trade } from '@wanswap/sdk'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ArrowDown, ChevronDown, ChevronUp, LogIn } from 'react-feather'
import ReactGA from 'react-ga'
import { Text } from 'rebass'
import { ThemeContext } from 'styled-components'
import AddressInputPanel from '../../components/AddressInputPanel'
import { ButtonError, ButtonLight, ButtonPrimary, ButtonConfirmed } from '../../components/Button'
import Card, { GreyCard } from '../../components/Card'
import Column, { AutoColumn } from '../../components/Column'
// import PairChart from '../../components/PairChart'
import ConfirmSwapModal from '../../components/swap/ConfirmSwapModal'
import CurrencyInputPanel from '../../components/CurrencyInputPanel'
import { SwapPoolTabs } from '../../components/NavigationTabs'
import { AutoRow, RowBetween } from '../../components/Row'
import AdvancedSwapDetailsDropdown from '../../components/swap/AdvancedSwapDetailsDropdown'
import BetterTradeLink, { DefaultVersionLink } from '../../components/swap/BetterTradeLink'
import confirmPriceImpactWithoutFee from '../../components/swap/confirmPriceImpactWithoutFee'
import { ArrowWrapper, SwapCallbackError, Wrapper } from '../../components/swap/styleds'
import TradePrice from '../../components/swap/TradePrice'
import TokenWarningModal from '../../components/TokenWarningModal'
import ProgressSteps from '../../components/ProgressSteps'
// import { useMedia } from 'react-use'
import { BETTER_TRADE_LINK_THRESHOLD, INITIAL_ALLOWED_SLIPPAGE } from '../../constants'
import { getTradeVersion, isTradeBetter } from '../../data/V1'
import { useActiveWeb3React } from '../../hooks'
import { useCurrency } from '../../hooks/Tokens'
import { ApprovalState, useApproveCallback, useApproveCallbackFromTrade } from '../../hooks/useApproveCallback'
import useENSAddress from '../../hooks/useENSAddress'
import { useSwapCallback } from '../../hooks/useSwapCallback'
import useToggledVersion, { DEFAULT_VERSION, Version } from '../../hooks/useToggledVersion'
import useWrapCallback, { WrapType } from '../../hooks/useWrapCallback'
import { useToggleSettingsMenu, useWalletModalToggle } from '../../state/application/hooks'
import { Field } from '../../state/swap/actions'
import {
  tryParseAmount,
  useDefaultsFromConvertURLSearch,
  useDerivedSwapInfo,
  useSwapActionHandlers,
  useSwapState
} from '../../state/swap/hooks'
import { useExpertModeManager, useUserSlippageTolerance } from '../../state/user/hooks'
import { LinkStyledButton, TYPE } from '../../theme'
import { maxAmountSpend } from '../../utils/maxAmountSpend'
import { computeTradePriceBreakdown, warningSeverity } from '../../utils/prices'
import { AppBodyV2 } from '../AppBody'
// import AppBody from '../AppBody'
import { ClickableText } from '../Pool/styleds'
import Loader from '../../components/Loader'
// import Panel from '../../components/Panel'
import { useTranslation } from 'react-i18next'
// import { usePairData } from '../../contexts/PairData'
import styled from 'styled-components'
// import { usePair } from '../../data/Reserves'
import { isMobile } from 'react-device-detect'
import { TOKEN_CONVERT_ADDRESS, TOKEN_CONVERT_ADDRESS_2 } from '../../constants/abis/token-convert'
import useTokenConvertCallback, { ConvertType } from '../../hooks/useTokenConvertCallback'
import useTokenConvertCallback2 from '../../hooks/useTokenConvertCallback2'

export default function ConvertWasp() {
  const loadedUrlParams = useDefaultsFromConvertURLSearch()

  // token warning stuff
  const [loadedInputCurrency, loadedOutputCurrency] = [
    useCurrency(loadedUrlParams?.inputCurrencyId),
    useCurrency(loadedUrlParams?.outputCurrencyId)
  ]
  const [dismissTokenWarning, setDismissTokenWarning] = useState<boolean>(false)
  const urlLoadedTokens: Token[] = useMemo(
    () => [loadedInputCurrency, loadedOutputCurrency]?.filter((c): c is Token => c instanceof Token) ?? [],
    [loadedInputCurrency, loadedOutputCurrency]
  )
  const handleConfirmTokenWarning = useCallback(() => {
    setDismissTokenWarning(true)
  }, [])

  const { account, chainId } = useActiveWeb3React()
  const theme = useContext(ThemeContext)
  const { t } = useTranslation()

  // toggle wallet when disconnected
  const toggleWalletModal = useWalletModalToggle()

  // for expert mode
  const toggleSettings = useToggleSettingsMenu()
  const [isExpertMode] = useExpertModeManager()

  // get custom setting values for user
  const [allowedSlippage] = useUserSlippageTolerance()

  // swap state
  const { independentField, typedValue, recipient } = useSwapState()
  const {
    v1Trade,
    v2Trade,
    currencyBalances,
    parsedAmount,
    currencies,
    inputError: swapInputError
  } = useDerivedSwapInfo()
  // const [, pair] = usePair(currencies[Field.INPUT], currencies[Field.OUTPUT])
  // const { token0, reserve0, reserve1 } = usePairData(pair?.liquidityToken?.address.toLowerCase()) // default is WAN-wanUSTD
  const inputAmount = useMemo(() => tryParseAmount(typedValue, currencies[Field.INPUT]), [currencies, typedValue])

  const [approvalConvert, approvalConvertCallback] = useApproveCallback(
    inputAmount,
    chainId && TOKEN_CONVERT_ADDRESS[chainId]
  )

  const [approvalConvert2, approvalConvertCallback2] = useApproveCallback(
    inputAmount,
    chainId && TOKEN_CONVERT_ADDRESS_2[chainId]
  )

  const { convertType, execute: onConvert, inputError: convertInputError } = useTokenConvertCallback(
    currencies[Field.INPUT],
    currencies[Field.OUTPUT],
    typedValue
  )

  const { convertType: convertType2, execute: onConvert2, inputError: convertInputError2 } = useTokenConvertCallback2(
    currencies[Field.INPUT],
    currencies[Field.OUTPUT],
    typedValue
  )
  const showConvert: boolean = convertType !== ConvertType.NOT_CONVERTABLE
  const showConvert2: boolean = convertType2 !== ConvertType.NOT_CONVERTABLE


  const { wrapType, execute: onWrap, inputError: wrapInputError } = useWrapCallback(
    currencies[Field.INPUT],
    currencies[Field.OUTPUT],
    typedValue
  )
  const showWrap: boolean = wrapType !== WrapType.NOT_APPLICABLE
  const { address: recipientAddress } = useENSAddress(recipient)
  const toggledVersion = useToggledVersion()
  const tradesByVersion = {
    [Version.v1]: v1Trade,
    [Version.v2]: v2Trade
  }
  const trade = showWrap || showConvert || showConvert2 ? undefined : tradesByVersion[toggledVersion]
  const defaultTrade = showWrap || showConvert || showConvert2 ? undefined : tradesByVersion[DEFAULT_VERSION]

  // TODO: remove
  const betterTradeLinkVersion: Version | undefined =
    toggledVersion === Version.v2 && isTradeBetter(v2Trade, v1Trade, BETTER_TRADE_LINK_THRESHOLD)
      ? Version.v1
      : toggledVersion === Version.v1 && isTradeBetter(v1Trade, v2Trade)
      ? Version.v2
      : undefined

  const parsedAmounts =
    showWrap || showConvert || showConvert2
      ? {
          [Field.INPUT]: parsedAmount,
          [Field.OUTPUT]: parsedAmount
        }
      : {
          [Field.INPUT]: independentField === Field.INPUT ? parsedAmount : trade?.inputAmount,
          [Field.OUTPUT]: independentField === Field.OUTPUT ? parsedAmount : trade?.outputAmount
        }

  const { onSwitchTokens, onCurrencySelection, onUserInput, onChangeRecipient } = useSwapActionHandlers()
  const isValid = !swapInputError
  const dependentField: Field = independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT

  const handleTypeInput = useCallback(
    (value: string) => {
      onUserInput(Field.INPUT, value)
    },
    [onUserInput]
  )
  const handleTypeOutput = useCallback(
    (value: string) => {
      onUserInput(Field.OUTPUT, value)
    },
    [onUserInput]
  )

  // modal and loading
  const [{ showConfirm, tradeToConfirm, swapErrorMessage, attemptingTxn, txHash }, setSwapState] = useState<{
    showConfirm: boolean
    tradeToConfirm: Trade | undefined
    attemptingTxn: boolean
    swapErrorMessage: string | undefined
    txHash: string | undefined
  }>({
    showConfirm: false,
    tradeToConfirm: undefined,
    attemptingTxn: false,
    swapErrorMessage: undefined,
    txHash: undefined
  })

  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]:
      showWrap || showConvert || showConvert2
        ? parsedAmounts[independentField]?.toExact() ?? ''
        : parsedAmounts[dependentField]?.toSignificant(6) ?? ''
  }

  const route = trade?.route
  const userHasSpecifiedInputOutput = Boolean(
    currencies[Field.INPUT] && currencies[Field.OUTPUT] && parsedAmounts[independentField]?.greaterThan(JSBI.BigInt(0))
  )
  const noRoute = !route

  // check whether the user has approved the router on the input token
  const [approval, approveCallback] = useApproveCallbackFromTrade(trade, allowedSlippage)

  // check if user has gone through approval process, used to show two step buttons, reset on token change
  const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)

  // mark when a user has submitted an approval, reset onTokenSelection for input field
  useEffect(() => {
    if (approval === ApprovalState.PENDING) {
      setApprovalSubmitted(true)
    }
  }, [approval, approvalSubmitted])

  const maxAmountInput: CurrencyAmount | undefined = maxAmountSpend(currencyBalances[Field.INPUT])
  const atMaxAmountInput = Boolean(maxAmountInput && parsedAmounts[Field.INPUT]?.equalTo(maxAmountInput))

  // the callback to execute the swap
  const { callback: swapCallback, error: swapCallbackError } = useSwapCallback(trade, allowedSlippage, recipient)

  const { priceImpactWithoutFee } = computeTradePriceBreakdown(trade)

  const handleSwap = useCallback(() => {
    if (priceImpactWithoutFee && !confirmPriceImpactWithoutFee(priceImpactWithoutFee)) {
      return
    }
    if (!swapCallback) {
      return
    }
    setSwapState({ attemptingTxn: true, tradeToConfirm, showConfirm, swapErrorMessage: undefined, txHash: undefined })
    swapCallback()
      .then(hash => {
        setSwapState({ attemptingTxn: false, tradeToConfirm, showConfirm, swapErrorMessage: undefined, txHash: hash })

        ReactGA.event({
          category: 'Swap',
          action:
            recipient === null
              ? 'Swap w/o Send'
              : (recipientAddress ?? recipient) === account
              ? 'Swap w/o Send + recipient'
              : 'Swap w/ Send',
          label: [
            trade?.inputAmount?.currency?.symbol,
            trade?.outputAmount?.currency?.symbol,
            getTradeVersion(trade)
          ].join('/')
        })
      })
      .catch(error => {
        setSwapState({
          attemptingTxn: false,
          tradeToConfirm,
          showConfirm,
          swapErrorMessage: error.message,
          txHash: undefined
        })
      })
  }, [tradeToConfirm, account, priceImpactWithoutFee, recipient, recipientAddress, showConfirm, swapCallback, trade])

  // errors
  const [showInverted, setShowInverted] = useState<boolean>(false)

  // warnings on slippage
  const priceImpactSeverity = warningSeverity(priceImpactWithoutFee)

  // show approve flow when: no error on inputs, not approved or pending, or approved in current session
  // never show if price impact is above threshold in non expert mode
  const showApproveFlow =
    !swapInputError &&
    (approval === ApprovalState.NOT_APPROVED ||
      approval === ApprovalState.PENDING ||
      (approvalSubmitted && approval === ApprovalState.APPROVED)) &&
    !(priceImpactSeverity > 3 && !isExpertMode)

  const handleConfirmDismiss = useCallback(() => {
    setSwapState({ showConfirm: false, tradeToConfirm, attemptingTxn, swapErrorMessage, txHash })
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onUserInput(Field.INPUT, '')
    }
  }, [attemptingTxn, onUserInput, swapErrorMessage, tradeToConfirm, txHash])

  const handleAcceptChanges = useCallback(() => {
    setSwapState({ tradeToConfirm: trade, swapErrorMessage, txHash, attemptingTxn, showConfirm })
  }, [attemptingTxn, showConfirm, swapErrorMessage, trade, txHash])

  const handleInputSelect = useCallback(
    inputCurrency => {
      setApprovalSubmitted(false) // reset 2 step UI for approvals
      onCurrencySelection(Field.INPUT, inputCurrency)
    },
    [onCurrencySelection]
  )

  const handleMaxInput = useCallback(() => {
    maxAmountInput && onUserInput(Field.INPUT, maxAmountInput.toExact())
  }, [maxAmountInput, onUserInput])

  const handleOutputSelect = useCallback(outputCurrency => onCurrencySelection(Field.OUTPUT, outputCurrency), [
    onCurrencySelection
  ])
  // const below1080 = useMedia('(max-width: 1080px)')

  return (
    <>
      <TokenWarningModal
        isOpen={urlLoadedTokens.length > 0 && urlLoadedTokens[0].symbol !== 'wanUSDT' && !dismissTokenWarning}
        tokens={urlLoadedTokens}
        onConfirm={handleConfirmTokenWarning}
      />
      <FlexRow>
        <SwapCon>
          <AppBodyV2>
            <SwapPoolTabs active={'swap'} />
            <Wrapper id="swap-page">
              <ConfirmSwapModal
                isOpen={showConfirm}
                trade={trade}
                originalTrade={tradeToConfirm}
                onAcceptChanges={handleAcceptChanges}
                attemptingTxn={attemptingTxn}
                txHash={txHash}
                recipient={recipient}
                allowedSlippage={allowedSlippage}
                onConfirm={handleSwap}
                swapErrorMessage={swapErrorMessage}
                onDismiss={handleConfirmDismiss}
              />

              <AutoColumnCus gap={'md'}>
                <CurrencyInputPanel
                  label={
                    independentField === Field.OUTPUT && !showWrap && !showConvert && !showConvert2 && trade
                      ? t('fromEstimated')
                      : t('from')
                  }
                  value={formattedAmounts[Field.INPUT]}
                  showMaxButton={!atMaxAmountInput}
                  currency={currencies[Field.INPUT]}
                  onUserInput={handleTypeInput}
                  onMax={handleMaxInput}
                  onCurrencySelect={handleInputSelect}
                  otherCurrency={currencies[Field.OUTPUT]}
                  id="swap-currency-input"
                />
                <AutoColumn
                  justify="space-between"
                  style={{ marginTop: '-30px', marginBottom: '-30px', position: 'relative', zIndex: 2 }}
                >
                  <AutoRow justify="center" style={{ padding: '0 1rem' }}>
                    <ArrowWrapper
                      onClick={() => {
                        setApprovalSubmitted(false) // reset 2 step UI for approvals
                        onSwitchTokens()
                      }}
                      clickable
                      style={{
                        borderRadius: '50%',
                        border: '1px solid white',
                        padding: 5,
                        marginTop: 0,
                        marginBottom: 0,
                        width: 35,
                        height: 35,
                        lineHeight: '25px',
                        paddingTop: 10,
                        display: 'inline-flex'
                      }}
                    >
                      <ChevronDown style={{ marginRight: '-5px' }} size="14" color={theme.text2} />
                      <ChevronUp size="14" color={theme.text2} />
                    </ArrowWrapper>
                  </AutoRow>
                </AutoColumn>
                <CurrencyInputPanel
                  value={formattedAmounts[Field.OUTPUT]}
                  onUserInput={handleTypeOutput}
                  label={
                    independentField === Field.INPUT && !showWrap && !showConvert && !showConvert2 && trade ? t('toEstimated') : t('to')
                  }
                  showMaxButton={false}
                  currency={currencies[Field.OUTPUT]}
                  onCurrencySelect={handleOutputSelect}
                  otherCurrency={currencies[Field.INPUT]}
                  id="swap-currency-output-2"
                />
                {recipient === null && !showWrap && !showConvert && !showConvert2 && isExpertMode ? (
                  <LinkStyledButton
                    style={{ color: '#C3C5CB' }}
                    id="add-recipient-button"
                    onClick={() => onChangeRecipient('')}
                  >
                    {t('addRecipient')}
                  </LinkStyledButton>
                ) : null}
                {recipient !== null && !showWrap && !showConvert && !showConvert2 ? (
                  <>
                    <AutoRow justify="space-between" style={{ padding: '0 1rem' }}>
                      <ArrowWrapper clickable={false}>
                        <ArrowDown size="16" color="#FFF" />
                      </ArrowWrapper>
                      <LinkStyledButton
                        style={{ color: '#C3C5CB', justifyContent: 'space-between' }}
                        id="remove-recipient-button"
                        onClick={() => onChangeRecipient(null)}
                      >
                        {t('removeRecipent')}
                      </LinkStyledButton>
                    </AutoRow>
                    <AddressInputPanel id="recipient" value={recipient} onChange={onChangeRecipient} />
                  </>
                ) : null}

                {showWrap || showConvert || showConvert2 ? null : (
                  <Card padding={'.25rem .75rem 0 .75rem'} borderRadius={'10px'}>
                    <AutoColumn gap="4px">
                      {Boolean(trade) && (
                        <RowBetween align="center">
                          <Text fontWeight={500} fontSize={14} color={theme.text2}>
                            {t('price')}
                          </Text>
                          <TradePrice
                            price={trade?.executionPrice}
                            showInverted={showInverted}
                            setShowInverted={setShowInverted}
                          />
                        </RowBetween>
                      )}
                      {allowedSlippage !== INITIAL_ALLOWED_SLIPPAGE && (
                        <RowBetween align="center">
                          <ClickableText fontWeight={500} fontSize={14} color={theme.text2} onClick={toggleSettings}>
                            {t('slippageTolerance')}
                          </ClickableText>
                          <ClickableText fontWeight={500} fontSize={14} color={theme.text2} onClick={toggleSettings}>
                            {allowedSlippage / 100}%
                          </ClickableText>
                        </RowBetween>
                      )}
                    </AutoColumn>
                  </Card>
                )}
              </AutoColumnCus>
              <BottomGroupingCus
                showWrap={(!showWrap && Boolean(trade)) || allowedSlippage !== INITIAL_ALLOWED_SLIPPAGE}
              >
                {!account ? (
                  <ButtonLight onClick={toggleWalletModal}>
                    <LogIn style={{ marginRight: 5 }} size="18" color="#ffffff" /> {t('connectWallet')}
                  </ButtonLight>
                ) : showWrap ? (
                  <ButtonPrimary disabled={Boolean(wrapInputError)} onClick={onWrap}>
                    {wrapInputError ??
                      (wrapType === WrapType.WRAP ? 'Wrap' : wrapType === WrapType.UNWRAP ? 'Unwrap' : null)}
                  </ButtonPrimary>
                ) : showConvert ? (
                  <RowBetween>
                    <ButtonConfirmed
                      onClick={approvalConvertCallback}
                      disabled={approvalConvert !== ApprovalState.NOT_APPROVED || approvalSubmitted}
                      width="48%"
                      altDisabledStyle={approvalConvert === ApprovalState.PENDING} // show solid button while waiting
                      confirmed={approvalConvert === ApprovalState.APPROVED}
                    >
                      {approvalConvert === ApprovalState.PENDING ? (
                        <AutoRow gap="6px" justify="center">
                          {t('approving')} <Loader stroke="white" />
                        </AutoRow>
                      ) : approvalSubmitted && approvalConvert === ApprovalState.APPROVED ? (
                        t('approved')
                      ) : (
                        t('approve') + ' ' + currencies[Field.INPUT]?.symbol
                      )}
                    </ButtonConfirmed>
                    <ButtonError
                      disabled={approvalConvert !== ApprovalState.APPROVED || Boolean(wrapInputError)}
                      onClick={onConvert}
                      width="48%"
                    >
                      {swapInputError ? swapInputError : convertInputError ? convertInputError :
                        (convertType === ConvertType.CONVERT
                          ? t('convert')
                          : convertType === ConvertType.REVERT
                          ? t('revert')
                          : null)}
                    </ButtonError>
                  </RowBetween>
                ) : showConvert2 ? (
                  <RowBetween>
                    <ButtonConfirmed
                      onClick={approvalConvertCallback2}
                      disabled={approvalConvert2 !== ApprovalState.NOT_APPROVED || approvalSubmitted}
                      width="48%"
                      altDisabledStyle={approvalConvert2 === ApprovalState.PENDING} // show solid button while waiting
                      confirmed={approvalConvert2 === ApprovalState.APPROVED}
                    >
                      {approvalConvert2 === ApprovalState.PENDING ? (
                        <AutoRow gap="6px" justify="center">
                          {t('approving')} <Loader stroke="white" />
                        </AutoRow>
                      ) : approvalSubmitted && approvalConvert2 === ApprovalState.APPROVED ? (
                        t('approved')
                      ) : (
                        t('approve') + ' ' + currencies[Field.INPUT]?.symbol
                      )}
                    </ButtonConfirmed>
                    <ButtonError
                      disabled={approvalConvert2 !== ApprovalState.APPROVED || Boolean(wrapInputError)}
                      onClick={onConvert2}
                      width="48%"
                    >
                      {swapInputError ? swapInputError : convertInputError2 ? convertInputError2 :
                        (convertType2 === ConvertType.CONVERT
                          ? t('convert')
                          : convertType2 === ConvertType.REVERT
                          ? t('revert')
                          : null)}
                    </ButtonError>
                  </RowBetween>
                ) : noRoute && userHasSpecifiedInputOutput ? (
                  <GreyCard style={{ textAlign: 'center' }}>
                    <TYPE.main mb="4px">{t('insufficientLiquidity')}</TYPE.main>
                  </GreyCard>
                ) : showApproveFlow ? (
                  <RowBetween>
                    <ButtonConfirmed
                      onClick={approveCallback}
                      disabled={approval !== ApprovalState.NOT_APPROVED || approvalSubmitted}
                      width="48%"
                      altDisabledStyle={approval === ApprovalState.PENDING} // show solid button while waiting
                      confirmed={approval === ApprovalState.APPROVED}
                    >
                      {approval === ApprovalState.PENDING ? (
                        <AutoRow gap="6px" justify="center">
                          {t('approving')} <Loader stroke="white" />
                        </AutoRow>
                      ) : approvalSubmitted && approval === ApprovalState.APPROVED ? (
                        t('approved')
                      ) : (
                        t('approve') + ' ' + currencies[Field.INPUT]?.symbol
                      )}
                    </ButtonConfirmed>
                    <ButtonError
                      onClick={() => {
                        if (isExpertMode) {
                          handleSwap()
                        } else {
                          setSwapState({
                            tradeToConfirm: trade,
                            attemptingTxn: false,
                            swapErrorMessage: undefined,
                            showConfirm: true,
                            txHash: undefined
                          })
                        }
                      }}
                      width="48%"
                      id="swap-button"
                      disabled={
                        !isValid || approval !== ApprovalState.APPROVED || (priceImpactSeverity > 3 && !isExpertMode)
                      }
                      error={isValid && priceImpactSeverity > 2}
                    >
                      <Text fontSize={16} fontWeight={500}>
                        {priceImpactSeverity > 3 && !isExpertMode
                          ? t('priceImpactHigh')
                          : t('swap') + `${priceImpactSeverity > 2 ? t('anyway') : ''}`}
                      </Text>
                    </ButtonError>
                  </RowBetween>
                ) : (
                  <ButtonError
                    onClick={() => {
                      if (isExpertMode) {
                        handleSwap()
                      } else {
                        setSwapState({
                          tradeToConfirm: trade,
                          attemptingTxn: false,
                          swapErrorMessage: undefined,
                          showConfirm: true,
                          txHash: undefined
                        })
                      }
                    }}
                    id="swap-button"
                    disabled={!isValid || (priceImpactSeverity > 3 && !isExpertMode) || !!swapCallbackError}
                    error={isValid && priceImpactSeverity > 2 && !swapCallbackError}
                  >
                    <Text fontSize={20} fontWeight={500}>
                      {swapInputError
                        ? swapInputError
                        : priceImpactSeverity > 3 && !isExpertMode
                        ? t('priceImpactTooHigh')
                        : t('swap') + `${priceImpactSeverity > 2 ? t('anyway') : ''}`}
                    </Text>
                  </ButtonError>
                )}
                {showApproveFlow && (
                  <Column style={{ marginTop: '1rem' }}>
                    <ProgressSteps steps={[approval === ApprovalState.APPROVED]} />
                  </Column>
                )}
                {isExpertMode && swapErrorMessage ? <SwapCallbackError error={swapErrorMessage} /> : null}
                {betterTradeLinkVersion ? (
                  <BetterTradeLink version={betterTradeLinkVersion} />
                ) : toggledVersion !== DEFAULT_VERSION && defaultTrade ? (
                  <DefaultVersionLink />
                ) : null}
              </BottomGroupingCus>
            </Wrapper>
          </AppBodyV2>
          <AdvancedSwapDetailsDropdown trade={trade} />
        </SwapCon>
      </FlexRow>
    </>
  )
}

const FlexRow = styled.div`
  display: flex;
  flex-direction: ${isMobile ? 'column-reverse' : 'row'};
  width: 100%;
  justify-content: center;
`

const SwapCon = styled.div`
  position: relative;
`

const AutoColumnCus = styled(AutoColumn)`
  grid-row-gap: 30px;
`

const BottomGroupingCus = styled.div<{
  showWrap: boolean
}>`
  margin-top: ${({ showWrap }) => (showWrap ? '1rem' : '2rem')};
`
